import React from "react";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";
import classnames from "classnames";
import { TeamSide, YardConverter } from "@prestosports/prestostats-core/Common";
import { useEventContext } from "../../../state/events/context";
import { useGameContextContext } from "../../../state/game-context/context";
import { useFootballContext } from "../../../state/football-play/context";
import { useTeamsContext } from "../../../state/teams/context";
import useDriveReplay from "./utils/useDriveReplay";
import useFieldConstants from "./utils/FieldConfiguration";
import DriveReplayControls from "./DriveReplayControls";
import DriveReplayPlayerYards from "./DriveReplayPlayerYards";
import { getFormattedTime } from "./utils/PlayUtils";
import QueryStringUtils from "../../../util/query-string-utils";
import driveReplayDialogStyles, {
	REPLAY_COLORS
} from "../styles/DriveReplayDialog.styles";

const VIEW_WIDTH = 1200;
const FIELD_TOP = 24;
const FIELD_BOTTOM = 196;
const TRAIL_Y = 212;
const SHOT_Y = 226;
// Room for each leg's arrow and the label sitting under it.
const LEG_ROW_HEIGHT = 22;

const isSpot = (yard) => yard !== null && yard !== undefined;

/**
 * Whether the play scored a touchdown, from the stat records the rules engine
 * wrote for it.
 *
 * A touchdown has no fragment of its own -- the engine derives it -- and
 * reading it off the ball reaching an end zone got it wrong: a kickoff whose
 * end spot is the goal line is not a touchdown, and neither is a touchback or a
 * safety. The engine already decided; ask it.
 */
const isTouchdown = (playId, driveStats) =>
	driveStats.some(
		(s) => s.playId === playId && (s.category || "").includes("touchdown")
	);

// A kick or a try that did not convert. Blocked counts: the ball is dead and
// nothing was scored either way.
const MISSED_STATES = [
	"FIELD_GOAL_MISSED",
	"FIELD_GOAL_BLOCKED",
	"PAT_MISSED",
	"PAT_BLOCKED",
	"PAT_POINTS_NOT_AWARDED",
	"DEUCE_MISSED"
];

const ordinal = (n) => ["", "1st", "2nd", "3rd", "4th"][n] || `${n}th`;

const ReplayField = ({
	replay,
	fieldLength,
	endZoneLength,
	swapTeams,
	spotName,
	ballColor,
	points,
	touchdown,
	homeLabel,
	awayLabel
}) => {
	const classes = driveReplayDialogStyles();
	const { step, steps, index, activeLeg } = replay;
	// Which way the team with the ball is going, in yard coordinates. A leg
	// running against it -- a defensive recovery and return -- is drawn in the
	// loss colour rather than the ball colour.
	const forward = step.homePossession ? 1 : -1;

	const total = fieldLength + endZoneLength * 2;
	// Same mapping the live field uses: yard 0 is the home goal line, and
	// swapTeams mirrors the drawing without touching the yard maths.
	const x = (yard) => {
		const base = ((endZoneLength + yard) / total) * VIEW_WIDTH;
		return swapTeams ? VIEW_WIDTH - base : base;
	};

	const bands = [];
	for (let yard = 0; yard < fieldLength; yard += 20) {
		const from = x(yard);
		const to = x(Math.min(yard + 10, fieldLength));
		bands.push(
			<rect
				key={`band-${yard}`}
				className={classes.turfBand}
				x={Math.min(from, to)}
				y={FIELD_TOP}
				width={Math.abs(to - from)}
				height={FIELD_BOTTOM - FIELD_TOP}
			/>
		);
	}

	const lines = [];
	const numbers = [];
	for (let yard = 0; yard <= fieldLength; yard += 5) {
		const whole = yard % 10 === 0;
		lines.push(
			<line
				key={`line-${yard}`}
				className={classes.chalk}
				x1={x(yard)}
				x2={x(yard)}
				y1={FIELD_TOP}
				y2={FIELD_BOTTOM}
				strokeWidth={whole ? 1.6 : 0.8}
				opacity={whole ? 0.5 : 0.28}
			/>
		);

		if (whole && yard > 0 && yard < fieldLength) {
			numbers.push(
				<text
					key={`num-${yard}`}
					className={classes.yardNumber}
					x={x(yard)}
					y={FIELD_TOP + 34}
					textAnchor="middle"
				>
					{Math.min(yard, fieldLength - yard)}
				</text>
			);
		}
	}

	const from = x(step.losYard);
	const to = x(step.path[activeLeg].yard);
	// The points only land once the ball has finished the play.
	const scored = activeLeg === step.path.length - 1;
	const scoredTouchdown = scored && touchdown;
	// A try that was worth something is a good one -- points only exist when it
	// converted. A field goal is a kick on a play that is not a try.
	const isTry = step.states.some((s) => s.startsWith("PAT"));
	const scoredTry = scored && points > 0 && isTry;
	const scoredFieldGoal =
		scored && points > 0 && !isTry && step.states.includes("FIELD_GOAL_GOOD");
	const missed = scored && step.states.some((s) => MISSED_STATES.includes(s));

	return (
		<svg
			className={classes.field}
			// Tall enough for however many legs this play has: a fumble, its
			// recovery and the return each take a row under the field.
			viewBox={`0 0 ${VIEW_WIDTH} ${Math.max(
				300,
				SHOT_Y + step.path.length * LEG_ROW_HEIGHT + 10
			)}`}
			role="img"
			aria-label={`Field showing ${step.title}`}
		>
			<defs>
				<marker
					id="driveReplayArrow"
					viewBox="0 0 10 10"
					refX="8"
					refY="5"
					markerWidth="5"
					markerHeight="5"
					orient="auto-start-reverse"
				>
					<path d="M 0 0 L 10 5 L 0 10 z" fill={REPLAY_COLORS.ball} />
				</marker>
				<marker
					id="driveReplayArrowBack"
					viewBox="0 0 10 10"
					refX="8"
					refY="5"
					markerWidth="5"
					markerHeight="5"
					orient="auto-start-reverse"
				>
					<path d="M 0 0 L 10 5 L 0 10 z" fill={REPLAY_COLORS.loss} />
				</marker>
			</defs>

			<rect
				className={classes.turf}
				x={0}
				y={FIELD_TOP}
				width={VIEW_WIDTH}
				height={FIELD_BOTTOM - FIELD_TOP}
			/>
			{bands}
			{/* The end zones sit at both extremes whichever way the field is drawn. */}
			{[0, VIEW_WIDTH - (endZoneLength / total) * VIEW_WIDTH].map((ex) => (
				<rect
					key={`ez-${ex}`}
					className={classes.endZone}
					x={ex}
					y={FIELD_TOP}
					width={(endZoneLength / total) * VIEW_WIDTH}
					height={FIELD_BOTTOM - FIELD_TOP}
				/>
			))}
			{lines}
			{numbers}

			{/* Whose end zone is whose. Positioned from the goal lines rather than
			    from the edges of the drawing, so they follow swapTeams with
			    everything else. */}
			{[
				{ label: homeLabel, centre: (x(0) + x(-endZoneLength)) / 2 },
				{
					label: awayLabel,
					centre: (x(fieldLength) + x(fieldLength + endZoneLength)) / 2
				}
			].map(({ label, centre }) => (
				<text
					key={`ez-label-${label}`}
					className={classes.endZoneLabel}
					x={centre}
					y={(FIELD_TOP + FIELD_BOTTOM) / 2}
					textAnchor="middle"
					transform={`rotate(-90 ${centre} ${(FIELD_TOP + FIELD_BOTTOM) / 2})`}
				>
					{(label || "").toUpperCase()}
				</text>
			))}

			{/* What the drive has already covered: one faint span per play walked,
			    each with what it gained, so the plays before this one stay
			    readable without competing with it. */}
			{steps.slice(0, index).map((prev) =>
				prev.losYard === prev.ballYard ? null : (
					<g key={`trail-${prev.id}`}>
						<line
							className={classes.trail}
							x1={x(prev.losYard)}
							x2={x(prev.ballYard)}
							y1={TRAIL_Y}
							y2={TRAIL_Y}
							strokeLinecap="round"
						/>
						<text
							className={classes.trailTag}
							x={(x(prev.losYard) + x(prev.ballYard)) / 2}
							y={TRAIL_Y - 9}
							textAnchor="middle"
						>
							{`${prev.gain > 0 ? "+" : ""}${prev.gain}`}
						</text>
					</g>
				)
			)}

			<line
				className={classes.moves}
				transform={`translate(${from} 0)`}
				x1={0}
				x2={0}
				y1={FIELD_TOP}
				y2={FIELD_BOTTOM}
				stroke={REPLAY_COLORS.los}
				strokeWidth={3}
			/>
			{isSpot(step.firstDownYard) && (
				<line
					className={classes.moves}
					transform={`translate(${x(step.firstDownYard)} 0)`}
					x1={0}
					x2={0}
					y1={FIELD_TOP}
					y2={FIELD_BOTTOM}
					stroke={REPLAY_COLORS.chains}
					strokeWidth={3}
					strokeDasharray="10 7"
				/>
			)}

			{/* One arrow per leg of the play: the rush, then the fumble, then the
			    recovery, then the return. Each draws itself out as the ball
			    reaches it, and the legs already covered stay on screen faded, so
			    the whole path is readable by the end of the play. Legs that did
			    not move the ball (a fumble where it was tackled) draw nothing. */}
			{step.path.slice(1, activeLeg + 1).map((leg, i) => {
				const legFrom = x(step.path[i].yard);
				const legTo = x(leg.yard);
				const length = Math.max(Math.abs(legTo - legFrom), 0.01);
				const current = i === activeLeg - 1;
				// Yards this leg covered, signed the way the team that snapped it
				// is going: a defensive return reads negative, like the play does.
				const legYards = (leg.yard - step.path[i].yard) * forward;
				const backwards = legYards < 0;
				// Each leg gets its own row. A catch and the return that follows it
				// cover the same ground in opposite directions, and drawn on one
				// line they sit on top of each other.
				const legY = SHOT_Y + i * LEG_ROW_HEIGHT;

				return legFrom === legTo ? null : (
					<g key={`leg-${step.id}-${leg.state}-${leg.yard}`}>
						<path
							className={current ? classes.shot : classes.shotDone}
							d={`M ${legFrom} ${legY} L ${legTo} ${legY}`}
							stroke={backwards ? REPLAY_COLORS.loss : REPLAY_COLORS.ball}
							markerEnd={
								backwards ? "url(#driveReplayArrowBack)" : "url(#driveReplayArrow)"
							}
							strokeDasharray={length}
							style={{ "--shot-length": length }}
						/>
						{/* What the leg covered above the arrow, what it was below it --
						    a rush, the catch, the return after a fumble -- so the
						    drawing reads without the sentence under the field. */}
						<text
							className={classes.legYards}
							x={(legFrom + legTo) / 2}
							y={legY - 6}
							textAnchor="middle"
							fill={backwards ? REPLAY_COLORS.loss : REPLAY_COLORS.ball}
						>
							{isSpot(leg.kickYards)
								? `${leg.kickYards} YD`
								: `${legYards > 0 ? "+" : ""}${legYards}`}
						</text>
						<text
							className={classes.legTag}
							x={(legFrom + legTo) / 2}
							y={legY + 14}
							textAnchor="middle"
							fill={backwards ? REPLAY_COLORS.loss : REPLAY_COLORS.ball}
						>
							{leg.label.toUpperCase()}
						</text>
					</g>
				);
			})}

			<g className={classes.moves} transform={`translate(${to} 0)`}>
				{/* The ball changed hands here: a ring opens out from it, twice, in
				    the colour of the team that now has it. Keyed on the leg so the
				    animation restarts on the leg it belongs to and nowhere else. */}
				{step.path[activeLeg].turnover && (
					<circle
						key={`turnover-${step.id}-${activeLeg}`}
						className={classes.turnoverPulse}
						cx={0}
						cy={(FIELD_TOP + FIELD_BOTTOM) / 2}
						r={13}
						fill="none"
						stroke={ballColor}
						strokeWidth={3}
					/>
				)}
				{/* The kick or try did not convert: the same ring, in the losing
				    colour, and no points to rise. */}
				{missed && (
					<circle
						key={`missed-${step.id}`}
						className={classes.scorePulse}
						cx={0}
						cy={(FIELD_TOP + FIELD_BOTTOM) / 2}
						r={13}
						fill="none"
						stroke={REPLAY_COLORS.loss}
						strokeWidth={3}
					/>
				)}
				{/* The play scored: the same ring in the scoring colour, and the
				    points rising off the top of the ball. Both wait for the last
				    leg -- the ball has to arrive before it is worth anything. */}
				{points > 0 && scored && (
					<>
						<circle
							key={`score-${step.id}`}
							className={classes.scorePulse}
							cx={0}
							cy={(FIELD_TOP + FIELD_BOTTOM) / 2}
							r={13}
							fill="none"
							stroke={REPLAY_COLORS.gain}
							strokeWidth={3}
						/>
						<text
							key={`points-${step.id}`}
							className={classes.scorePoints}
							x={0}
							y={(FIELD_TOP + FIELD_BOTTOM) / 2 - 20}
							textAnchor="middle"
						>
							{`+${points}`}
						</text>
					</>
				)}
				{/* The ball wears the colour of whoever has it, outlined like its
				    labels so a team colour close to the turf still reads. */}
				<circle
					className={classes.ball}
					cx={0}
					cy={(FIELD_TOP + FIELD_BOTTOM) / 2}
					r={13}
					fill={ballColor}
				/>
				<text
					className={classes.ballLabel}
					x={0}
					y={(FIELD_TOP + FIELD_BOTTOM) / 2 - 22}
					textAnchor="middle"
				>
					{/* On a score the spot is beside the point: the ball is in the end
					    zone, and saying so beats naming a yard line. */}
					{(scoredTouchdown && "TOUCHDOWN") ||
						(scoredTry && "PAT GOOD") ||
						(scoredFieldGoal && "FIELD GOAL") ||
						(missed && "NO GOOD") ||
						spotName(step.path[activeLeg].yard)}
				</text>
				{/* Who has the ball on this leg, under it: the receiver on the catch,
				    the defender on the recovery, the returner on the run back. */}
				<text
					className={classes.ballCarrier}
					x={0}
					y={(FIELD_TOP + FIELD_BOTTOM) / 2 + 32}
					textAnchor="middle"
				>
					{step.path[activeLeg].carrier}
				</text>
			</g>
		</svg>
	);
};

const DriveReplayDialog = () => {
	const classes = driveReplayDialogStyles();

	const {
		state: footballState,
		stopDriveReplay,
		setReplayIndex
	} = useFootballContext();
	const driveStats = footballState.driveReplayStats() || [];

	const { state: eventState } = useEventContext();
	const currentEventSettings = eventState.currentEventSettings();

	const { state: gameContextState } = useGameContextContext();
	const swapTeams = gameContextState.swapTeams();
	const gameConfig = gameContextState.gameConfig();

	const { state: teamState } = useTeamsContext();
	const homeTeam = teamState.homeTeam();
	const awayTeam = teamState.awayTeam();

	const { fieldLength, endZoneLength } = useFieldConstants(currentEventSettings);
	// Gated with the play list's button, and with the debug panel itself: the
	// whole feature is behind ?debug while it settles.
	const isDebug = QueryStringUtils.useIsDebug();
	// The field's geometry goes in so a scoring play -- which records no spot of
	// its own -- can be given a leg to the uprights or the goal line.
	const replay = useDriveReplay({ fieldLength, endZoneLength });

	if (!isDebug || !replay.active || !fieldLength) {
		return null;
	}

	const { step, steps, index, count } = replay;

	const yardConverter = new YardConverter(fieldLength);

	const spotName = (yard) => {
		if (!isSpot(yard)) {
			return "—";
		}

		// A kick's leg carries the ball through the uprights, past the goal
		// line: there is no yard line out there to name.
		if (yard < 0 || yard > fieldLength) {
			return "Goal";
		}

		const { teamSide, yard: relative } =
			yardConverter.convertFromFieldPosition(yard);
		const team = teamSide === TeamSide.Home ? homeTeam : awayTeam;

		return `${team?.abbrName?.substring(0, 3) || teamSide} ${relative}`;
	};

	const distanceLabel = (s) => {
		if (s.isDriveStart || !isSpot(s.firstDownYard)) {
			return null;
		}

		const toGo = Math.abs(s.firstDownYard - s.losYard);
		const goalLine = s.firstDownYard <= 0 || s.firstDownYard >= fieldLength;

		return `${ordinal(s.down)} & ${goalLine ? "Goal" : toGo}`;
	};

	const gainLabel = (s) => {
		if (s.isDriveStart) {
			return "Drive start";
		}

		return s.gain === 0 ? "No gain" : `${s.gain > 0 ? "+" : ""}${s.gain} yd`;
	};

	const gainColor = (s) => {
		if (s.isDriveStart || s.gain === 0) {
			return undefined;
		}

		return { color: s.gain > 0 ? REPLAY_COLORS.gain : REPLAY_COLORS.loss };
	};

	// What the play was worth, from the stat records the rules engine wrote, so
	// the number follows the ruleset rather than a table of assumptions here.
	// The largest wins rather than the sum: the same points can be recorded
	// against both the team and the player.
	//
	// A touchdown is the exception: its stat carries no points value (the
	// boxscore adds the configured touchdown value itself), so it is read off
	// the same end-zone test the TOUCHDOWN tag uses.
	const touchdown = isTouchdown(step.id, driveStats);
	const scoredPoints = driveStats
		.filter((s) => s.playId === step.id)
		.reduce((most, s) => Math.max(most, s.values?.points || 0), 0);
	const points =
		scoredPoints || (touchdown ? gameConfig?.scoring?.touchdown ?? 6 : 0);

	const possessionTeam = step.homePossession ? homeTeam : awayTeam;
	// The ball takes the colour of whoever is holding it on this leg, so a lost
	// fumble changes the ball's colour as it changes hands.
	const holdingTeamId = step.path[replay.activeLeg]?.teamId;
	const holdingTeam = [homeTeam, awayTeam].find((t) => t?.id === holdingTeamId);
	const ballColor =
		holdingTeam?.color || possessionTeam?.color || REPLAY_COLORS.ball;

	return (
		<Dialog
			open
			onClose={stopDriveReplay}
			fullWidth
			maxWidth="lg"
			classes={{ paper: classes.paper }}
			aria-labelledby="drive-replay-title"
		>
			<DialogTitle disableTypography className={classes.title}>
				<div className={classes.titleText}>
					<Typography variant="h6" id="drive-replay-title">
						{`Drive replay — ${possessionTeam?.teamName || ""}`}
					</Typography>
					<Typography variant="body2" color="textSecondary">
						{`Play ${index + 1} of ${count}`}
					</Typography>
				</div>
				<IconButton
					size="small"
					aria-label="Close drive replay"
					onClick={stopDriveReplay}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers className={classes.content}>
				<div className={classes.fieldWrapper}>
					<ReplayField
						replay={replay}
						fieldLength={fieldLength}
						endZoneLength={endZoneLength}
						swapTeams={swapTeams}
						spotName={spotName}
						ballColor={ballColor}
						points={points}
						touchdown={touchdown}
						homeLabel={homeTeam?.abbrName || homeTeam?.teamName || "Home"}
						awayLabel={awayTeam?.abbrName || awayTeam?.teamName || "Visitor"}
					/>
				</div>

				{/* The play in words beside what the drive has cost or gained each
				    player. Fixed height: the account runs from one line to five
				    depending on the play, and letting the row grow with it made
				    everything below it jump between plays. */}
				<div className={classes.detailRow}>
					{/* One clause per fragment rather than a single blob: a fumble, its
					    recovery and the return read as a sequence, and the first clause
					    carries the play itself. HTML rather than SVG text, which cannot
					    wrap. */}
					<div className={classnames(classes.card, classes.description)}>
						{(step.descriptionParts.length
							? step.descriptionParts
							: [step.title]
						).map((part, i) => (
							<Typography
								key={part}
								variant="body1"
								className={i === 0 ? classes.descriptionLead : classes.descriptionPart}
							>
								{part}
							</Typography>
						))}
					</div>

					<DriveReplayPlayerYards />
				</div>

				<div className={classes.readouts}>
					<div className={classes.card}>
						<div className={classes.chips}>
							{distanceLabel(step) && (
								<Chip
									size="small"
									variant="outlined"
									label={distanceLabel(step)}
									style={{ color: REPLAY_COLORS.los, borderColor: REPLAY_COLORS.los }}
								/>
							)}
							<Chip
								size="small"
								variant="outlined"
								label={`Q${step.period} ${getFormattedTime(
									step.time,
									gameConfig,
									step.period
								)}`}
							/>
							{/* No play-type chips here: every arrow on the field names
							    itself now, and repeating the same words in the card just
							    doubles them. */}
							<Chip
								size="small"
								variant="outlined"
								label={gainLabel(step)}
								style={
									gainColor(step) && {
										...gainColor(step),
										borderColor: gainColor(step).color
									}
								}
							/>
						</div>
						{/* The field carries the sentence; the card names the players in
						    it, so a crew can see who was involved at a glance. */}
						<Typography variant="body1" className={classes.narration}>
							{step.participants.join("  ·  ") || step.title}
						</Typography>
						<Typography variant="body2" className={classes.spots}>
							{`${spotName(step.losYard)} → ${spotName(step.ballYard)}${
								isSpot(step.firstDownYard)
									? `   ·   line to gain ${spotName(step.firstDownYard)}`
									: ""
							}`}
						</Typography>
					</div>

					<div className={classnames(classes.card, classes.tally)}>
						<Box>
							<Typography variant="caption" color="textSecondary">
								Plays
							</Typography>
							<Typography variant="h6" className={classes.statValue}>
								{replay.playsSoFar}
							</Typography>
						</Box>
						<Box>
							<Typography variant="caption" color="textSecondary">
								Net yards
							</Typography>
							<Typography variant="h6" className={classes.statValue}>
								{replay.yardsSoFar}
							</Typography>
						</Box>
						<Box>
							<Typography variant="caption" color="textSecondary">
								Ball on
							</Typography>
							<Typography variant="body1" className={classes.statValue}>
								{spotName(step.ballYard)}
							</Typography>
						</Box>
						<Box>
							<Typography variant="caption" color="textSecondary">
								Drive
							</Typography>
							<Typography variant="body1" className={classes.statValue}>
								{step.isDriveEnd ? "Ended" : "In progress"}
							</Typography>
						</Box>
					</div>
				</div>

				<div className={classes.strip}>
					{steps.map((s, i) => (
						<Button
							key={s.id}
							variant="outlined"
							size="small"
							className={classnames(classes.tick, {
								[classes.tickCurrent]: i === index
							})}
							// MUI stacks a button's contents in a row by default; the
							// tick reads as a label over a value.
							classes={{ label: classes.tickLabel }}
							aria-current={i === index}
							onClick={() => setReplayIndex(i)}
						>
							<span className={classes.tickDown}>
								{s.isDriveStart ? "Start" : distanceLabel(s)}
							</span>
							<span className={classes.tickGain} style={gainColor(s)}>
								{s.actions[0] || gainLabel(s)}
							</span>
							<span className={classes.tickSpot}>{spotName(s.ballYard)}</span>
						</Button>
					))}
				</div>

				<DriveReplayControls replay={replay} />
			</DialogContent>
		</Dialog>
	);
};

export default DriveReplayDialog;
